import { AiProvider } from '@Model';
import {
    Button,
    isNullOrEmpty,
    LabeledDiv,
    Modal,
    Progress,
    SingleSelect,
    TextInputBase,
} from '@synqo/frontend-component-library';
import { useCallback, useState } from 'react';

const aiProvidersArray: Array<{ value: AiProvider; label: string; name: string }> = [
    {
        value: AiProvider.Azure,
        label: 'Azure',
        name: 'azure',
    },
];

const hasMultipleAiProviders = aiProvidersArray.length > 1;

interface GenerateWithAiModalProps {
    isOpen: boolean;
    close: () => void;

    description: string;

    onGenerate: (prompt: string, provider: AiProvider) => void;
    isGenerating: boolean;
}

export function GenerateWithAiModal({
    isOpen,
    description,
    close,
    onGenerate,
    isGenerating,
}: GenerateWithAiModalProps) {
    const [prompt, setPrompt] = useState('');
    const [provider, setProvider] = useState<AiProvider>(AiProvider.Azure);

    const onChangeProvider = useCallback((value: AiProvider) => {
        setProvider(value);
        return true;
    }, []);

    const handleCloseModal = useCallback(() => {
        if (isGenerating) {
            return;
        }

        setPrompt('');
        close();
    }, [isGenerating, setPrompt, close]);

    const handleStartGenerate = useCallback(() => {
        onGenerate(prompt.trim(), provider);
    }, [onGenerate, prompt, provider]);

    const hasPrompt = !isNullOrEmpty(prompt.trim());

    return (
        <Modal
            title="Mit KI generieren"
            isOpen={isOpen}
            onClose={handleCloseModal}
            withoutCloseButton={isGenerating}
            footer={
                <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                    <Button onClick={close}>Abbrechen</Button>
                    <Button onClick={handleStartGenerate} locked={isGenerating || !hasPrompt}>
                        {isGenerating ? 'Generiere...' : 'Generieren'}
                    </Button>
                </div>
            }
        >
            <div style={{ marginBottom: '1rem' }}>
                <p style={{ margin: 0, color: 'var(--mantine-color-gray-7)' }}>{description}</p>
            </div>

            {hasMultipleAiProviders && (
                <div style={{ marginBottom: '1rem' }}>
                    <LabeledDiv label="KI-Anbieter" htmlFor="ai-provider-select">
                        <SingleSelect<AiProvider>
                            name="ai-provider-select"
                            value={provider}
                            onChange={onChangeProvider}
                            items={aiProvidersArray}
                            locked={isGenerating}
                        />
                    </LabeledDiv>
                </div>
            )}
            <div style={{ marginBottom: '2rem' }}>
                <TextInputBase
                    name="prompt"
                    label="Prompt"
                    value={prompt}
                    onChange={(v) => {
                        setPrompt(v);
                        return true;
                    }}
                    readOnly={isGenerating}
                    inputSize={15}
                    renderUndo={false}
                />
            </div>
            {isGenerating && (
                <div style={{ marginTop: '1rem', marginBottom: '2rem' }}>
                    <LabeledDiv label="KI-Anfrage läuft..." htmlFor={'progress-bar'}>
                        <Progress id={'progress-bar'} animated size="md" ariaLabel="AI request is running..." />
                    </LabeledDiv>
                </div>
            )}
        </Modal>
    );
}
